import { POWER_UP_DEFS, type PowerUpType } from '../shared';

const POWER_UP_ICONS: Record<PowerUpType, string> = {
  star:     '\u2605',
  speed:    '\u26A1',
  fireball: '\uD83D\uDD25',
  iceball:  '\u2744\uFE0F',
  shield:   '\uD83D\uDEE1\uFE0F',
};

const POWER_UP_DESCRIPTIONS: Record<PowerUpType, string> = {
  star:     'Become invisible to enemies',
  speed:    'Double movement speed',
  fireball: 'Enhanced bullet damage',
  iceball:  'Freeze enemies on hit',
  shield:   'Absorb incoming damage',
};

export class HelpWindow {
  private overlay: HTMLDivElement;
  private contentArea!: HTMLDivElement;
  private tabs: HTMLDivElement[] = [];
  private visible = false;
  private activeTab = 0;

  constructor() {
    this.injectStyles();
    this.overlay = this.buildDOM();
    document.body.appendChild(this.overlay);
    this.setupKeyboard();
  }

  /* ── CSS ──────────────────────────────────────────────── */

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .help-kbd {
        display: inline-block;
        background: rgba(255,255,255,0.08);
        border: 1px solid rgba(255,255,255,0.15);
        border-radius: 4px;
        padding: 2px 8px;
        font-family: inherit;
        font-size: 11px;
        font-weight: 700;
        color: rgba(255,255,255,0.7);
        line-height: 1.6;
        min-width: 20px;
        text-align: center;
      }
      .help-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 7px 0;
        border-bottom: 1px solid rgba(255,255,255,0.04);
      }
      .help-row:last-child { border-bottom: none; }
      .help-row-label {
        font-size: 12px;
        color: rgba(255,255,255,0.55);
      }
      .help-row-keys {
        display: flex;
        gap: 4px;
        align-items: center;
      }
      .help-section-title {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.3);
        margin: 18px 0 8px;
      }
      .help-section-title:first-child { margin-top: 0; }
      .help-mode-card {
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 12px;
        padding: 16px 18px;
        margin-bottom: 12px;
      }
      .help-mode-card h3 {
        font-size: 14px;
        font-weight: 800;
        margin: 0 0 8px;
      }
      .help-mode-card ul {
        margin: 0;
        padding-left: 18px;
      }
      .help-mode-card li {
        font-size: 12px;
        color: rgba(255,255,255,0.55);
        line-height: 1.7;
      }
      .help-powerup-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(105px, 1fr));
        gap: 10px;
      }
      .help-powerup-card {
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 10px;
        padding: 12px 10px;
        text-align: center;
      }
      .help-powerup-icon {
        font-size: 22px;
        margin-bottom: 4px;
      }
      .help-powerup-name {
        font-size: 11px;
        font-weight: 700;
        margin-bottom: 2px;
      }
      .help-powerup-desc {
        font-size: 10px;
        color: rgba(255,255,255,0.4);
        line-height: 1.4;
      }
      .help-powerup-dur {
        font-size: 9px;
        color: rgba(255,255,255,0.25);
        margin-top: 4px;
      }
      .help-mobile-section { display: none; }
      @media (max-width: 600px) {
        .help-desktop-section { display: none !important; }
        .help-mobile-section  { display: block !important; }
        .help-powerup-grid { grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); }
      }
    `;
    document.head.appendChild(style);
  }

  /* ── DOM ──────────────────────────────────────────────── */

  private buildDOM(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.id = 'help-overlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'none',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '50px',
      zIndex: '25',
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(12px)',
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hide();
    });

    // Panel
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position: 'relative',
      background: 'rgba(12,12,22,0.95)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '20px',
      padding: '28px 32px 24px',
      width: '620px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      overflowY: 'auto',
      pointerEvents: 'all',
    });

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u2715';
    Object.assign(closeBtn.style, {
      position: 'absolute',
      top: '14px',
      right: '14px',
      background: 'rgba(255,255,255,0.08)',
      border: 'none',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '16px',
      width: '32px',
      height: '32px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
    closeBtn.addEventListener('click', () => this.hide());
    panel.appendChild(closeBtn);

    // Title
    const title = document.createElement('h2');
    title.textContent = 'HOW TO PLAY';
    Object.assign(title.style, {
      fontSize: '18px',
      fontWeight: '900',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      margin: '0 0 20px',
      background: 'linear-gradient(135deg, #4dd0e1, #80deea)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textAlign: 'center',
    });
    panel.appendChild(title);

    // Tab bar
    const tabBar = document.createElement('div');
    Object.assign(tabBar.style, {
      display: 'flex',
      gap: '0',
      marginBottom: '20px',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    });

    const tabLabels = ['Controls', 'Game Modes', 'Power-Ups'];
    tabLabels.forEach((label, i) => {
      const tab = document.createElement('div');
      tab.textContent = label;
      Object.assign(tab.style, {
        flex: '1',
        padding: '10px 0',
        textAlign: 'center',
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: i === 0 ? '#4dd0e1' : 'rgba(255,255,255,0.3)',
        cursor: 'pointer',
        borderBottom: i === 0 ? '2px solid #4dd0e1' : '2px solid transparent',
        transition: 'color 0.15s, border-color 0.15s',
        userSelect: 'none',
      });
      tab.addEventListener('click', () => this.switchTab(i));
      tabBar.appendChild(tab);
      this.tabs.push(tab);
    });
    panel.appendChild(tabBar);

    // Content area
    this.contentArea = document.createElement('div');
    panel.appendChild(this.contentArea);

    overlay.appendChild(panel);

    // Render initial tab
    this.renderTab(0);

    return overlay;
  }

  /* ── Tab switching ───────────────────────────────────── */

  private switchTab(index: number): void {
    this.tabs.forEach((t, i) => {
      t.style.color = i === index ? '#4dd0e1' : 'rgba(255,255,255,0.3)';
      t.style.borderBottomColor = i === index ? '#4dd0e1' : 'transparent';
    });
    this.activeTab = index;
    this.renderTab(index);
  }

  private renderTab(index: number): void {
    this.contentArea.innerHTML = '';
    switch (index) {
      case 0: this.contentArea.appendChild(this.buildControlsTab()); break;
      case 1: this.contentArea.appendChild(this.buildGameModesTab()); break;
      case 2: this.contentArea.appendChild(this.buildPowerUpsTab()); break;
    }
  }

  /* ── Controls tab ────────────────────────────────────── */

  private buildControlsTab(): HTMLElement {
    const frag = document.createElement('div');

    // Desktop section
    const desktop = document.createElement('div');
    desktop.className = 'help-desktop-section';

    const movTitle = document.createElement('div');
    movTitle.className = 'help-section-title';
    movTitle.textContent = 'Movement';
    desktop.appendChild(movTitle);

    const movementKeys: [string, string[]][] = [
      ['Move Forward',   ['W']],
      ['Move Backward',  ['S']],
      ['Strafe Left',    ['A']],
      ['Strafe Right',   ['D']],
      ['Turn Left',      ['Q', 'J', '\u2190']],
      ['Turn Right',     ['E', 'L', '\u2192']],
      ['Camera Up',      ['I', '\u2191']],
      ['Camera Down',    ['K', '\u2193']],
    ];
    movementKeys.forEach(([label, keys]) => desktop.appendChild(this.makeRow(label, keys)));

    const actTitle = document.createElement('div');
    actTitle.className = 'help-section-title';
    actTitle.textContent = 'Actions';
    desktop.appendChild(actTitle);

    const actionKeys: [string, string[]][] = [
      ['Fire / Shoot',       ['Space', 'LMB']],
      ['Stats',              ['1']],
      ['Upgrade Menu',       ['2', 'U']],
      ['Auto-Fire',          ['F']],
      ['Leaderboard',        ['Tab']],
      ['Help',               ['H']],
      ['Close Menus',        ['Esc']],
    ];
    actionKeys.forEach(([label, keys]) => desktop.appendChild(this.makeRow(label, keys)));

    frag.appendChild(desktop);

    // Mobile section
    const mobile = document.createElement('div');
    mobile.className = 'help-mobile-section';

    const mTitle = document.createElement('div');
    mTitle.className = 'help-section-title';
    mTitle.textContent = 'Touch Controls';
    mobile.appendChild(mTitle);

    const touchControls: [string, string][] = [
      ['\uD83D\uDD79\uFE0F  Left Joystick', 'Move your tank in any direction'],
      ['\uD83D\uDD79\uFE0F  Right Joystick', 'Turn your tank and tilt the camera'],
      ['\uD83D\uDD34  Fire Button', 'Tap to shoot (bottom-right)'],
    ];
    touchControls.forEach(([title, desc]) => {
      const row = document.createElement('div');
      row.style.cssText = 'padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.04);';
      const t = document.createElement('div');
      t.style.cssText = 'font-size:12px;font-weight:700;color:rgba(255,255,255,0.7);';
      t.textContent = title;
      const d = document.createElement('div');
      d.style.cssText = 'font-size:11px;color:rgba(255,255,255,0.4);margin-top:2px;';
      d.textContent = desc;
      row.appendChild(t);
      row.appendChild(d);
      mobile.appendChild(row);
    });

    frag.appendChild(mobile);
    return frag;
  }

  private makeRow(label: string, keys: string[]): HTMLElement {
    const row = document.createElement('div');
    row.className = 'help-row';

    const lbl = document.createElement('span');
    lbl.className = 'help-row-label';
    lbl.textContent = label;
    row.appendChild(lbl);

    const keysDiv = document.createElement('span');
    keysDiv.className = 'help-row-keys';
    keys.forEach((k, i) => {
      if (i > 0) {
        const sep = document.createElement('span');
        sep.style.cssText = 'font-size:10px;color:rgba(255,255,255,0.2);';
        sep.textContent = '/';
        keysDiv.appendChild(sep);
      }
      const kbd = document.createElement('span');
      kbd.className = 'help-kbd';
      kbd.textContent = k;
      keysDiv.appendChild(kbd);
    });
    row.appendChild(keysDiv);

    return row;
  }

  /* ── Game Modes tab ──────────────────────────────────── */

  private buildGameModesTab(): HTMLElement {
    const frag = document.createElement('div');

    // FFA
    const ffa = document.createElement('div');
    ffa.className = 'help-mode-card';
    ffa.innerHTML = `
      <h3 style="color:#ff5252;">\uD83D\uDC80 Free For All</h3>
      <ul>
        <li>Every player fights for themselves</li>
        <li>Destroy tanks and objects to earn crystals and XP</li>
        <li>Level up and buy upgrades to grow stronger</li>
        <li>Collect power-ups scattered across the arena</li>
        <li>The player with the most kills dominates the leaderboard</li>
      </ul>
    `;
    frag.appendChild(ffa);

    // CTF
    const ctf = document.createElement('div');
    ctf.className = 'help-mode-card';
    ctf.innerHTML = `
      <h3 style="color:#4dd0e1;">\uD83D\uDEA9 Capture the Flag</h3>
      <ul>
        <li>Two teams: <span style="color:#e84040;font-weight:700;">Red</span> vs <span style="color:#4080e8;font-weight:700;">Blue</span></li>
        <li>Grab the enemy flag and bring it back to your base to score</li>
        <li>Your flag must be at your base to capture</li>
        <li>Touch a dropped friendly flag to return it home</li>
        <li>First team to <strong>3 captures</strong> wins, or highest score at 10 minutes</li>
        <li>You can still earn crystals and upgrades during the match</li>
      </ul>
    `;
    frag.appendChild(ctf);

    return frag;
  }

  /* ── Power-Ups tab ───────────────────────────────────── */

  private buildPowerUpsTab(): HTMLElement {
    const frag = document.createElement('div');

    // Upgrades overview
    const upTitle = document.createElement('div');
    upTitle.className = 'help-section-title';
    upTitle.textContent = 'Upgrade System';
    frag.appendChild(upTitle);

    const upDesc = document.createElement('div');
    upDesc.style.cssText = 'font-size:12px;color:rgba(255,255,255,0.5);line-height:1.7;margin-bottom:16px;';
    upDesc.innerHTML = `
      Destroy enemies and objects to earn <span style="color:#4dd0e1;font-weight:700;">crystals</span>.
      Press <span class="help-kbd">2</span> or <span class="help-kbd">U</span> to open the upgrade menu.<br>
      Choose from <strong>Weapons</strong>, <strong>Chassis</strong>, <strong>Movement</strong>,
      <strong>Attachments</strong>, and <strong>Stat upgrades</strong> to customize your build.
    `;
    frag.appendChild(upDesc);

    // Power-ups
    const puTitle = document.createElement('div');
    puTitle.className = 'help-section-title';
    puTitle.textContent = 'Arena Power-Ups';
    frag.appendChild(puTitle);

    const puDesc = document.createElement('div');
    puDesc.style.cssText = 'font-size:11px;color:rgba(255,255,255,0.35);margin-bottom:12px;';
    puDesc.textContent = 'Destroy glowing boxes in the arena to activate power-ups.';
    frag.appendChild(puDesc);

    const grid = document.createElement('div');
    grid.className = 'help-powerup-grid';

    (Object.keys(POWER_UP_DEFS) as PowerUpType[]).forEach((type) => {
      const def = POWER_UP_DEFS[type];
      const [r, g, b] = def.color;

      const card = document.createElement('div');
      card.className = 'help-powerup-card';
      card.style.borderColor = `rgba(${r * 255 | 0},${g * 255 | 0},${b * 255 | 0},0.2)`;

      const icon = document.createElement('div');
      icon.className = 'help-powerup-icon';
      icon.textContent = POWER_UP_ICONS[type];
      card.appendChild(icon);

      const name = document.createElement('div');
      name.className = 'help-powerup-name';
      name.style.color = `rgb(${r * 255 | 0},${g * 255 | 0},${b * 255 | 0})`;
      name.textContent = def.label;
      card.appendChild(name);

      const desc = document.createElement('div');
      desc.className = 'help-powerup-desc';
      desc.textContent = POWER_UP_DESCRIPTIONS[type];
      card.appendChild(desc);

      const dur = document.createElement('div');
      dur.className = 'help-powerup-dur';
      dur.textContent = `${def.duration}s`;
      card.appendChild(dur);

      grid.appendChild(card);
    });

    frag.appendChild(grid);
    return frag;
  }

  /* ── Keyboard ────────────────────────────────────────── */

  private setupKeyboard(): void {
    document.addEventListener('keydown', (e) => {
      if (e.code === 'KeyH' && !e.repeat) {
        // Don't toggle if typing in an input
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        this.toggle();
      }
      if (e.code === 'Escape' && this.visible) {
        this.hide();
      }
    });
  }

  /* ── Public API ──────────────────────────────────────── */

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  show(): void {
    this.visible = true;
    this.overlay.style.display = 'flex';
  }

  hide(): void {
    this.visible = false;
    this.overlay.style.display = 'none';
  }

  get isOpen(): boolean {
    return this.visible;
  }
}
